// Links Claude wrote on a card: each shows its host, and one that isn't in the email is marked.
// The server lists the card's links it found in the thread (`CardMailCheck.linksInEmail`, in the
// same `URL.href` spelling `safeHref` returns), so a link is checked by plain lookup.
import { createContext, useContext } from "react";
import type { CardMailCheck } from "@shared/api";
import { citationHost } from "./research";

/** The links of the card's email, or null where there is nothing to compare (no thread). */
export const EmailLinksContext = createContext<ReadonlySet<string> | null>(null);

export function useEmailLinks(): ReadonlySet<string> | null {
  return useContext(EmailLinksContext);
}

export function emailLinkSet(check: CardMailCheck | null | undefined): ReadonlySet<string> | null {
  return check ? new Set(check.linksInEmail) : null;
}

export type LinkMark = "in_email" | "not_in_email" | "unchecked";

export function linkMark(href: string, inEmail: ReadonlySet<string> | null): LinkMark {
  if (!inEmail) return "unchecked";
  return inEmail.has(href) ? "in_email" : "not_in_email";
}

/** "example.com" for a link, without "www.". */
export const linkHost = citationHost;

/** Whether the label already shows where the link goes (a bare URL needs no host after it). */
export function labelShowsUrl(label: string, href: string): boolean {
  const flat = label.trim().replace(/\/$/, "");
  return flat === href.replace(/\/$/, "") || /^https?:\/\//i.test(flat);
}

/** The words after a link, e.g. "example.com ↗" or "example.com ↗ · not in the email". */
export function linkSuffix(label: string, href: string, mark: LinkMark): string {
  const parts = [labelShowsUrl(label, href) ? "↗" : `${linkHost(href)} ↗`];
  if (mark === "not_in_email") parts.push("not in the email");
  return parts.join(" · ");
}
